/**
 * MobileDesktopBanner
 *
 * Detects mobile viewport (< 768px) and shows a contextual recommendation.
 *
 * State machine:
 *   First visit  (localStorage.first_mobile_visit NOT set)  → full-screen overlay
 *   Return visit (flag set, sessionStorage.mobile_banner_dismissed NOT set) → compact top banner
 *   Dismissed    (sessionStorage flag set) OR desktop → render nothing
 *
 * "Send me the link" → POST /api/user/send-desktop-link → email + Sent ✓ countdown
 * "Continue on mobile" (full-screen) → sets localStorage flag, shows compact banner
 * "Continue on mobile →" (compact) → sets sessionStorage flag, hides for session
 */
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Monitor, Mail, Check, Loader2, X } from "lucide-react";
import { API } from "@/App";

function isMobileViewport() {
  // Allow forced demo mode via URL param for visual testing
  if (new URLSearchParams(window.location.search).get("mobile_demo") === "1") {
    return true;
  }
  return (
    window.innerWidth < 768 ||
    /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  );
}

export default function MobileDesktopBanner() {
  const location = useLocation();
  const [mobile] = useState(isMobileViewport);
  const [firstVisit, setFirstVisit] = useState(
    () => !localStorage.getItem("first_mobile_visit")
  );
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("mobile_banner_dismissed") === "true"
  );

  // Send-link state
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  // Nothing to show on desktop
  if (!mobile || dismissed || location.pathname.startsWith("/admin")) return null;

  const showFullScreen = firstVisit;
  const showBanner = !firstVisit;

  // ─── actions ─────────────────────────────────────────────────────────────

  const markFirstVisit = () => {
    localStorage.setItem("first_mobile_visit", "true");
    setFirstVisit(false);
  };

  const handleContinueFromFullScreen = () => {
    markFirstVisit();
    // showBanner becomes true automatically
  };

  const handleDismissBanner = () => {
    sessionStorage.setItem("mobile_banner_dismissed", "true");
    setDismissed(true);
  };

  const handleSendLink = async () => {
    if (sending || (sent && countdown > 0)) return;
    setSending(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/user/send-desktop-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ url: window.location.href }),
      });
      const data = await res.json();
      if (res.ok) {
        setSentTo(data.sent_to || "your email");
        setSent(true);
        markFirstVisit();
        setCountdown(60);
        timerRef.current = setInterval(() => {
          setCountdown((c) => {
            if (c <= 1) {
              clearInterval(timerRef.current);
              return 0;
            }
            return c - 1;
          });
        }, 1000);
      }
    } catch {
      // Silently fail — banner is not critical
    } finally {
      setSending(false);
    }
  };

  const sendLabel = () => {
    if (sending) return null; // shows spinner
    if (sent && countdown > 0) return `Sent ✓ (${countdown}s)`;
    if (sent && countdown === 0) return "Resend link";
    return "Send me the link";
  };

  // ─── Shared send button ───────────────────────────────────────────────────
  const SendButton = ({ compact = false }) => (
    <button
      onClick={handleSendLink}
      disabled={sending || (sent && countdown > 0)}
      data-testid="mobile-banner-send-btn"
      className={`
        inline-flex items-center justify-center gap-2 font-medium rounded-sm transition-all
        ${compact
          ? "h-8 px-4 text-xs bg-white text-slate-900 hover:bg-slate-100"
          : "h-11 px-6 text-sm bg-white text-slate-900 hover:bg-slate-100"}
        disabled:opacity-60 disabled:cursor-not-allowed
      `}
    >
      {sending ? (
        <Loader2 size={compact ? 12 : 14} className="animate-spin" />
      ) : sent && countdown > 0 ? (
        <>
          <Check size={compact ? 12 : 14} className="text-emerald-600" />
          {sendLabel()}
        </>
      ) : (
        <>
          <Mail size={compact ? 12 : 14} />
          {sendLabel()}
        </>
      )}
    </button>
  );

  // ─── Full-screen overlay (first visit) ───────────────────────────────────
  if (showFullScreen) {
    return (
      <div
        className="fixed inset-0 z-[9999] bg-slate-950 flex flex-col items-center justify-center px-8 text-center"
        data-testid="mobile-banner-fullscreen"
      >
        {/* Icon */}
        <div className="w-14 h-14 rounded-sm bg-slate-800 border border-slate-700 flex items-center justify-center mb-8">
          <Monitor size={24} className="text-slate-300" />
        </div>

        <h2 className="text-xl font-semibold text-white mb-4 leading-tight tracking-tight">
          Welcome to BioDataStudio
        </h2>

        <p className="text-slate-300 text-sm leading-relaxed mb-2 max-w-xs">
          You're on a mobile device. BioDataStudio is a desktop-first
          intelligence platform with interactive reports, competitive landscape
          maps, and downloadable data packages.
        </p>
        <p className="text-slate-400 text-sm leading-relaxed mb-10 max-w-xs">
          For the best experience, open this on your laptop or desktop computer.
        </p>

        {/* Send link confirmation */}
        {sent && sentTo && (
          <p className="text-emerald-400 text-xs font-mono mb-4 bg-emerald-950/40 border border-emerald-900/50 px-4 py-2 rounded-sm">
            Link sent to {sentTo}
          </p>
        )}

        {/* Primary CTA */}
        <SendButton />

        {/* Secondary */}
        <button
          onClick={handleContinueFromFullScreen}
          data-testid="mobile-banner-continue-fullscreen"
          className="mt-5 text-slate-400 hover:text-slate-200 text-sm transition-colors"
        >
          Continue on mobile anyway →
        </button>
      </div>
    );
  }

  // ─── Compact top banner (return visits) ──────────────────────────────────
  if (showBanner) {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-[200] bg-slate-900 border-b border-slate-800"
        data-testid="mobile-banner-compact"
      >
        <div className="px-4 py-3 flex items-center gap-3">
          <Monitor size={14} className="text-slate-400 shrink-0" />
          <p className="text-slate-300 text-xs leading-tight flex-1 min-w-0">
            <span className="font-medium text-white">Built for desktop.</span>
            {" "}Open on your laptop for the full experience.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            {/* Send link confirmation */}
            {sent && sentTo && (
              <span className="text-emerald-400 text-[10px] font-mono hidden sm:inline">
                Sent to {sentTo}
              </span>
            )}
            <SendButton compact />
            <button
              onClick={handleDismissBanner}
              data-testid="mobile-banner-dismiss-btn"
              className="text-slate-500 hover:text-slate-300 transition-colors p-1"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
